import { Alert, App as AntApp, DatePicker, Form, InputNumber, Modal, Select } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { toAmount } from "../app/balance.ts";
import { billingKeys, livingOptions, type LivingOption, type Tariff } from "../app/billing.ts";
import { useCourses } from "../app/classes.ts";

/** Field names match the API so server field errors land under the right input. */
interface TariffForm {
  courseId?: string;
  grade?: number;
  livingOption?: LivingOption;
  monthlyAmount?: number;
  validFrom?: Dayjs;
}

interface Props {
  /** "new" adds a version; a tariff corrects that version's amount. */
  target: Tariff | "new" | undefined;
  onClose(): void;
}

/** Adds a tariff version from a date (the previous version of the key closes the day before) or corrects an unused one. */
export function TariffModal({ target, onClose }: Props) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const courses = useCourses();
  const [form] = Form.useForm<TariffForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const existing = target !== undefined && target !== "new" ? target : undefined;
  const courseId = Form.useWatch("courseId", form);
  const grades = courses.data?.find((c) => c.id === courseId)?.allowedGrades.map(Number) ?? [];

  useEffect(() => {
    if (target === undefined) return;
    form.resetFields();
    if (existing) form.setFieldsValue({ monthlyAmount: toAmount(existing.monthlyAmount) });
    setSummary([]);
  }, [target, existing, form]);

  const save = useMutation({
    mutationFn: async (values: TariffForm) => {
      const result = existing
        ? await api.PUT("/billing/tariffs/{id}", { params: { path: { id: existing.id } }, body: { monthlyAmount: values.monthlyAmount ?? null } })
        : await api.POST("/billing/tariffs", {
            body: {
              courseId: values.courseId ?? null,
              grade: values.grade ?? null,
              livingOption: values.livingOption ?? null,
              monthlyAmount: values.monthlyAmount ?? null,
              validFrom: values.validFrom?.format("YYYY-MM-DD") ?? null,
            },
          });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: billingKeys.all }), queryClient.invalidateQueries({ queryKey: ["audit"] })]);
      void message.success(t(existing ? "billing.saved" : "billing.versionAdded"));
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  const required = [{ required: true, message: t("common.required") }];
  return (
    <Modal
      open={target !== undefined}
      title={existing ? t("billing.tariffs.editTitle", { course: existing.courseName, grade: existing.grade }) : t("billing.tariffs.createTitle")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {summary.length > 0 && <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} title={summary.map((m) => <div key={m}>{m}</div>)} />}
      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={existing ? t("billing.editHint") : t("billing.newVersionHint")} />
      <Form<TariffForm> form={form} layout="vertical" requiredMark={false} onFinish={(values) => save.mutate(values)}>
        {!existing && (
          <>
            <Form.Item name="courseId" label={t("billing.tariffs.course")} rules={required}>
              <Select
                loading={courses.isPending}
                showSearch={{ optionFilterProp: "label" }}
                options={(courses.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
                onChange={() => form.setFieldValue("grade", undefined)}
              />
            </Form.Item>
            <Form.Item name="grade" label={t("billing.tariffs.grade")} rules={required} extra={courseId ? undefined : t("billing.tariffs.gradeHint")}>
              <Select disabled={!courseId} options={grades.map((g) => ({ value: g, label: t("billing.gradeLabel", { grade: g }) }))} />
            </Form.Item>
            <Form.Item name="livingOption" label={t("billing.tariffs.livingOption")} rules={required} extra={t("billing.tariffs.livingOptionHint")}>
              <Select options={livingOptions.map((o) => ({ value: o, label: t(`billing.livingOptions.${o}`) }))} />
            </Form.Item>
          </>
        )}
        <Form.Item name="monthlyAmount" label={t("billing.tariffs.monthlyAmount")} rules={required}>
          <InputNumber<number> style={{ width: "100%" }} min={0} precision={2} step={10000} />
        </Form.Item>
        {!existing && (
          <Form.Item name="validFrom" label={t("billing.validFrom")} rules={required} extra={t("billing.validFromHint")}>
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
